import { grantsFreeMagnets } from '../../../../utils/offerPricing';
import { routes } from '../../../../routes';

// Decide whether to show the elementary "Your 2 FREE photo magnets" page after a
// package is built and before Announcements & Gifts (issue #5218 item 8). The
// elementary sibling of seniors/Builder/buyAllPromoVisibility.js: a pure
// function so the gate is unit-testable, and asked at EVERY entry point (the
// built package, the All Digital fast path, the gallery's Download-all card).
//
// `qualifies` / `hasFreeMagnets` are EXPLICIT, FRESH booleans (from
// freeMagnetsStatus -- the build/buy-all response or a re-read of the cart
// after the package exists). Never read off a cached cart object: the React
// Query cache and CustomerContext predate the package that was just built,
// and their `qualifiesForFreeMagnets` was a stale false that hid the page
// for a family's first package.
//
// Shown only when ALL of:
//   - the live offer grants free magnets (grantsFreeMagnets -- the ONE
//     interpreter; nothing else decides), AND
//   - the cart qualifies (explicitly true), AND
//   - the cart does not already carry the offer's free sheet, AND
//   - the session is not all-JPG (those poses have their background baked in
//     and are not sold on magnets, exactly as the Buy All card is hidden).
//
// The server re-checks on accept (Cart#sync_free_magnets!), so a wrong answer
// here can only show the page, never attach a sheet the offer does not back.
export default function shouldShowFreeMagnets({
  offer, qualifies, hasFreeMagnets, allJpg,
}) {
  if (!grantsFreeMagnets(offer)) return false;
  if (qualifies !== true) return false;
  if (hasFreeMagnets) return false;
  if (allJpg) return false;
  return true;
}

// May the page take its show/skip decision yet? The skip effect must never run
// on placeholder data: usePoses serves placeholderData [] while the real fetch
// is in flight, and deciding on it skipped the page for every customer (the
// Timber Point canary, 2026-09-01). Pure so the readiness contract is testable.
export function readyToDecide({
  loadingPackage, packageId, loadingOffer, loadingCart, poses, posesPlaceholder,
  addonsPlaceholder = false, backgroundsPlaceholder = false,
}) {
  if (loadingPackage || loadingOffer || loadingCart) return false;
  if (!packageId) return false;
  if (!poses || posesPlaceholder) return false;
  // useHolidayAddons / useBackgrounds also serve placeholderData [] while
  // fetching; rendering on them flashes fallback art/value and an absent
  // background strip (review on #5264). Same rule as poses: real data only.
  if (addonsPlaceholder || backgroundsPlaceholder) return false;
  return true;
}

// Where to go after a package lands in the cart: the magnet page (carrying
// `next`, where it continues to) when the gate says so, else `next` itself.
// `status` is freeMagnetsStatus()'s { qualifies, hasFreeMagnets }. ONE
// implementation for every elementary entry point.

export function afterElementaryBuildRoute({
  next, orderPackageId, offer, status, allJpg,
}) {
  if (!orderPackageId) return next;
  const show = shouldShowFreeMagnets({
    offer, qualifies: status?.qualifies, hasFreeMagnets: status?.hasFreeMagnets, allJpg,
  });
  if (!show) return next;
  return routes.freeMagnets(orderPackageId, { next });
}
